import { memo, useCallback, useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import { FiltersList } from "app/pages/sales-intelligence/modules/advanced-search-filtering/components/FiltersList/FiltersList";
import { FilterPanelContentContainer } from "app/pages/sales-intelligence/modules/advanced-search-filtering/containers/FilterPanelContent/FilterPanelContentContainer";
import { useFiltersManager } from "app/pages/sales-intelligence/modules/advanced-search-filtering/hooks/useFiltersManager";
import { setIsFiltersPanelExpanded } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/action-creators";
import { selectIsFiltersPanelExpanded } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import { createIosFilterConfig } from "app/pages/sales-intelligence/modules/find-apps-filters/configs/createIosFilterConfig";
import withFindAppsTracking from "app/pages/sales-intelligence/modules/find-apps-filters/hoc/withFindAppsTracking";
import useUpdateTabState from "app/pages/sales-intelligence/modules/find-apps-filters/hooks/useUpdateTabState";
import useFindAppsDescriptionQuery from "app/pages/sales-intelligence/modules/find-apps-filters/queries/useGetAppsDescription";
import {
    selectFindAppsIosExpandedFilter,
    selectFindAppsIosFiltersValue,
} from "app/pages/sales-intelligence/store/find-apps/selectors";
import { AppsSourceType } from "app/pages/sales-intelligence/store/find-apps/types/appsSourceType";
import type { IAppsIosFiltersDto } from "app/pages/sales-intelligence/store/find-apps/types/iosFilters.dto";

const WrappedFindAppsFiltersList = withFindAppsTracking(FiltersList<IAppsIosFiltersDto>);

const FindAppsIosFiltersContainer = () => {
    const dispatch = useDispatch();

    const isFilterPanelExpanded = useSelector(selectIsFiltersPanelExpanded);
    const filtersValue: Partial<IAppsIosFiltersDto> = useSelector(selectFindAppsIosFiltersValue);
    const expandedFilter: string | null = useSelector(selectFindAppsIosExpandedFilter);

    const { handleFilterUpdate, handleExpandFilter } = useUpdateTabState<IAppsIosFiltersDto>(AppsSourceType.AppleStore);

    const { data: description, isFetching } = useFindAppsDescriptionQuery();

    const filtersConfig = useMemo(() => createIosFilterConfig(description), [description]);

    const filtersManager = useFiltersManager<IAppsIosFiltersDto>({
        filtersValue,
        filtersConfig,
        onFiltersUpdate: handleFilterUpdate,
    });

    const handleFilterPanelToggle = useCallback((isExpanded: boolean) => {
        dispatch(setIsFiltersPanelExpanded(isExpanded));
    }, []);

    return (
        <FilterPanelContentContainer<IAppsIosFiltersDto>
            isExpanded={isFilterPanelExpanded}
            filtersManager={filtersManager}
            onPanelToggle={handleFilterPanelToggle}
        >
            <WrappedFindAppsFiltersList
                isLoading={isFetching}
                trackingTab={AppsSourceType.AppleStore}
                filtersManager={filtersManager}
                expandedFilter={expandedFilter}
                onToggleExpand={(filterName) => {
                    handleExpandFilter(String(filterName) === expandedFilter ? null : String(filterName));
                }}
                onClearFilter={(key) => filtersManager.resetFilter(key)}
                onFilterUpdate={(key, value) => filtersManager.updateFilter(key, value)}
            />
        </FilterPanelContentContainer>
    );
};

export default memo(FindAppsIosFiltersContainer);
